import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWorkflows, saveWorkflows, useOrgId } from '../../lib/data';
import { DEFAULT_GRAPHS } from './defaults';
import { migrateLegacyGraphs } from './legacy';
import { nodeDataForKind } from './nodeTypes';
import type { WorkflowEdge, WorkflowGraph, WorkflowNode } from './types';

/**
 * Shared workflow-collection state for the list + editor pages. Owns load,
 * legacy migration, default seeding, and persistence so both pages read from
 * one source of truth.
 *
 * Persistence split:
 *  - create / duplicate / delete save IMMEDIATELY (await the PUT) so navigating
 *    list → editor right after creating a graph can't race the editor's refetch.
 *  - updateGraph (node/edge edits in the editor) is debounced (700ms) via the
 *    autosave effect; the editor calls flushSave() on unmount to persist any
 *    pending edit before the list refetches.
 *
 * Each consumer mounts its own instance (the pages are separate views); the
 * collection lives in D1, so a refetch on navigation is cheap and correct.
 */
export function useWorkflows() {
  const orgId = useOrgId();
  const [graphs, setGraphs] = useState<WorkflowGraph[]>(DEFAULT_GRAPHS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadedRef = useRef(false);
  const dirtyRef = useRef(false);
  const graphsRef = useRef<WorkflowGraph[]>(DEFAULT_GRAPHS);

  // Keep a ref so timers / unmount flushes / immediate saves read the latest.
  useEffect(() => { graphsRef.current = graphs; }, [graphs]);

  // ---- load + migrate + seed (per-org; reloads on org switch) ----
  useEffect(() => {
    if (!orgId) { setLoading(true); return; }
    loadedRef.current = false; // disable autosave until this org's graphs load
    setError(null);
    (async () => {
      try {
        const rows = await fetchWorkflows(orgId);
        const usable = migrateLegacyGraphs(rows);
        const reseeded = usable !== rows; // empty/stale → returned DEFAULT_GRAPHS
        graphsRef.current = usable;
        setGraphs(usable);
        setSeeded(reseeded);
        loadedRef.current = true; // enable autosave only after a real load
        if (reseeded) {
          dirtyRef.current = true; // persist the reseed back to D1
        }
      } catch {
        // Worker unreachable / errored: keep in-memory defaults for browsing,
        // but DO NOT enable autosave — otherwise the next edit would PUT these
        // defaults and wipe whatever real rows are in D1 once the Worker
        // recovers. loadedRef stays false → autosave stays disabled.
        graphsRef.current = DEFAULT_GRAPHS;
        setGraphs(DEFAULT_GRAPHS);
        setError('Could not load workflows (Worker unreachable) — editing disabled until reload.');
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  // ---- debounced autosave (only after a real load + a dirtying edit) ----
  useEffect(() => {
    if (!loadedRef.current || !dirtyRef.current || !orgId) return;
    const t = setTimeout(() => {
      dirtyRef.current = false;
      saveWorkflows(graphsRef.current, orgId)
        .then(() => setSaveError(null))
        .catch(() => {
          dirtyRef.current = true; // retry on next edit
          setSaveError('Save failed — your last edit may not have persisted.');
        });
    }, 700);
    return () => clearTimeout(t);
  }, [graphs, orgId]);

  const persist = useCallback(async (next: WorkflowGraph[]) => {
    graphsRef.current = next;
    setGraphs(next);
    if (orgId && loadedRef.current) {
      try { await saveWorkflows(next, orgId); setSaveError(null); }
      catch { setSaveError('Save failed — your change may not have persisted.'); }
    }
  }, [orgId]);

  const createGraph = useCallback(async (): Promise<string> => {
    const id = `flow-${Date.now()}`;
    const graph: WorkflowGraph = {
      id,
      name: 'Untitled Workflow',
      description: 'A new automation flow. Click to edit.',
      category: 'Custom',
      iconKey: 'webhook',
      iconClass: 'w-4 h-4 text-purple-600',
      nodes: [{
        id: `trigger-${id}`,
        type: 'workflow',
        position: { x: 120, y: 160 },
        data: nodeDataForKind('trigger'),
      }],
      edges: [],
    };
    await persist([graph, ...graphsRef.current]);
    return id;
  }, [persist]);

  const duplicateGraph = useCallback(async (sourceId: string): Promise<string | null> => {
    const src = graphsRef.current.find((g) => g.id === sourceId);
    if (!src) return null;
    const id = `flow-${Date.now()}`;
    const copy: WorkflowGraph = {
      ...JSON.parse(JSON.stringify(src)) as WorkflowGraph,
      id,
      name: `${src.name} (Copy)`,
    };
    await persist([copy, ...graphsRef.current]);
    return id;
  }, [persist]);

  const deleteGraph = useCallback(async (id: string): Promise<void> => {
    await persist(graphsRef.current.filter((g) => g.id !== id));
  }, [persist]);

  /** Editor canvas edits: local update + flag dirty (debounced autosave). */
  const updateGraph = useCallback((id: string, patch: { nodes: WorkflowNode[]; edges: WorkflowEdge[] }) => {
    setGraphs((prev) => prev.map((g) => (g.id === id ? { ...g, nodes: patch.nodes, edges: patch.edges } : g)));
    dirtyRef.current = true;
  }, []);

  /** Force-save any pending debounced edit (editor calls this on unmount). */
  const flushSave = useCallback(async () => {
    if (!loadedRef.current || !dirtyRef.current || !orgId) return;
    dirtyRef.current = false;
    try { await saveWorkflows(graphsRef.current, orgId); }
    catch { dirtyRef.current = true; }
  }, [orgId]);

  return { graphs, loading, error, seeded, saveError, createGraph, duplicateGraph, deleteGraph, updateGraph, flushSave };
}
